import React, { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { GETNET, getSpeedLabel, getDurationLabel } from '../data/plans'
import './Step2Summary.css'

/* ── Icons ── */
const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const PhonePayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
    <line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
)

const ArrowRight = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
)

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
)

const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

const ZapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"
    aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)

const QrCodeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
    <line x1="14" y1="14" x2="14" y2="14"/><line x1="17" y1="14" x2="17" y2="14"/>
    <line x1="20" y1="14" x2="20" y2="14"/><line x1="20" y1="17" x2="20" y2="17"/>
    <line x1="17" y1="20" x2="20" y2="20"/><line x1="14" y1="17" x2="14" y2="20"/>
  </svg>
)

/* ── UPI deep-link builder ── */
function buildUpiUri({ upiId, name, amount }) {
  // Manual encoding: spaces as %20, not +
  const encode = (s) => encodeURIComponent(s)
  return `upi://pay?pa=${encode(upiId)}&pn=${encode(name)}&am=${encode(String(amount))}&cu=INR&tn=${encode('GetNet Broadband Plan Payment')}`
}

/* ── iOS-specific UPI app deep links ── */
function buildAppLinks({ upiId, name, amount }) {
  const encode = (s) => encodeURIComponent(s)
  const base = `pa=${encode(upiId)}&pn=${encode(name)}&am=${encode(String(amount))}&cu=INR&tn=${encode('GetNet Broadband Plan Payment')}`
  return {
    phonepe: `phonepe://pay?${base}`,
    gpay:    `tez://upi/pay?${base}`,
    paytm:   `paytmmp://pay?${base}`,
  }
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

/* ── Mobile detection hook ──
   Uses three signals to catch all phones:
   1. Screen width ≤ 768px
   2. Touch support
   3. UA string contains mobile keywords
   Any ONE signal being true → treat as mobile (show UPI button)
──────────────────────────────────────────── */
function useIsMobile() {
  function detect() {
    const narrowScreen = window.innerWidth <= 768
    const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window
    const uaMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
      navigator.userAgent
    )
    return narrowScreen || hasTouch || uaMobile
  }

  const [isMobile, setIsMobile] = useState(detect)

  useEffect(() => {
    const handler = () => setIsMobile(detect())
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  return isMobile
}

/* ── Component ── */
const Step2Summary = ({ order, onNext, onBack }) => {
  const [copied, setCopied]         = useState(false)
  const [qrExpanded, setQrExpanded] = useState(false)
  const isMobile                    = useIsMobile()
  const ios                         = isIOS()

  const upiUri  = buildUpiUri({ upiId: GETNET.upiId, name: GETNET.name, amount: order.price })
  const appLinks = buildAppLinks({ upiId: GETNET.upiId, name: GETNET.name, amount: order.price })

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(GETNET.upiId)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = GETNET.upiId
      ta.style.cssText = 'position:fixed;opacity:0'
      document.body.appendChild(ta)
      ta.focus(); ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  // Android: single upi:// intent works across all apps
  const handleUpiPay = () => { window.location.href = upiUri }

  // iOS: each app has its own scheme; open in new tab so Safari doesn't navigate away
  const handleIosApp = (url) => {
    const a = document.createElement('a')
    a.href = url
    a.click()
  }

  /* ── Shared sub-components ── */
  const UpiIdRow = () => (
    <div className="step2__upi-section">
      <span className="step2__upi-label">UPI ID</span>
      <div className="step2__upi-row">
        <code className="step2__upi-id">{GETNET.upiId}</code>
        <button
          type="button"
          className={`step2__copy-btn ${copied ? 'step2__copy-btn--copied' : ''}`}
          onClick={handleCopy}
          aria-label={copied ? 'Copied!' : `Copy UPI ID: ${GETNET.upiId}`}
          title={copied ? 'Copied!' : 'Copy UPI ID'}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
    </div>
  )

  const AppsHint = () => (
    <p className="step2__apps-hint" aria-label="Accepted UPI apps">
      PhonePe &middot; Google Pay &middot; Paytm &middot; BHIM
    </p>
  )

  const PaidBtn = () => (
    <button type="button" className="step2__paid-btn" onClick={onNext}>
      <span>I Have Paid — Confirm via WhatsApp</span>
      <ArrowRight />
    </button>
  )

  return (
    <section className="step2" aria-labelledby="step2-heading">
      <div className="step2__layout">

        {/* ── Left / Top: Order Summary ── */}
        <div className="step2__summary-card">
          <div className="step2__summary-header">
            <h2 id="step2-heading" className="step2__summary-title">Order Summary</h2>
          </div>

          <dl className="step2__summary-list">
            <div className="step2__summary-row">
              <dt className="step2__summary-key">Name</dt>
              <dd className="step2__summary-val">{order.name}</dd>
            </div>
            <div className="step2__summary-row">
              <dt className="step2__summary-key">Mobile</dt>
              <dd className="step2__summary-val">+91 {order.phone}</dd>
            </div>
            <div className="step2__summary-row">
              <dt className="step2__summary-key">Plan Speed</dt>
              <dd className="step2__summary-val step2__summary-val--highlight">{getSpeedLabel(order.speed)}</dd>
            </div>
            <div className="step2__summary-row">
              <dt className="step2__summary-key">Duration</dt>
              <dd className="step2__summary-val">{getDurationLabel(order.duration)}</dd>
            </div>
          </dl>

          <div className="step2__total-row">
            <span className="step2__total-label">Total Amount</span>
            <span className="step2__total-value" aria-label={`Total: ${order.price} rupees`}>
              &#8377;{order.price.toLocaleString('en-IN')}
            </span>
          </div>

          {/* How to Pay — different text for mobile vs desktop */}
          <div className="step2__instructions">
            <h3 className="step2__instructions-title">How to Pay</h3>
            {isMobile ? (
              <ol className="step2__instructions-list">
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">1</span>
                  Tap <strong>Open UPI App</strong> below
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">2</span>
                  Choose PhonePe, Google Pay, Paytm or any UPI app
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">3</span>
                  Pay exactly <strong>&#8377;{order.price.toLocaleString('en-IN')}</strong>
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">4</span>
                  Return here and tap <strong>I Have Paid</strong>
                </li>
              </ol>
            ) : (
              <ol className="step2__instructions-list">
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">1</span>
                  Open PhonePe, Google Pay or Paytm on your phone
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">2</span>
                  Tap <strong>Scan QR</strong> and scan the code on the right
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">3</span>
                  Pay exactly <strong>&#8377;{order.price.toLocaleString('en-IN')}</strong>
                </li>
                <li className="step2__instructions-item">
                  <span className="step2__instructions-num" aria-hidden="true">4</span>
                  Click <strong>I Have Paid</strong> and confirm via WhatsApp
                </li>
              </ol>
            )}
          </div>
        </div>

        {/* ── Right / Bottom: Payment panel ── */}
        <div className="step2__qr-card">

          {isMobile ? (
            /* ════════════ MOBILE LAYOUT ════════════ */
            <>
              <div className="step2__qr-header">
                <PhonePayIcon />
                <span>Pay Now</span>
              </div>

              {/* iOS: individual app buttons; Android: single upi:// intent */}
              {ios ? (
                <div className="step2__ios-apps">
                  <p className="step2__ios-label">Choose your UPI app to pay &#8377;{order.price.toLocaleString('en-IN')}</p>
                  <div className="step2__ios-btns">
                    <button type="button" className="step2__ios-app-btn" onClick={() => handleIosApp(appLinks.phonepe)}>
                      <ZapIcon /><span>PhonePe</span>
                    </button>
                    <button type="button" className="step2__ios-app-btn" onClick={() => handleIosApp(appLinks.gpay)}>
                      <ZapIcon /><span>Google Pay</span>
                    </button>
                    <button type="button" className="step2__ios-app-btn" onClick={() => handleIosApp(appLinks.paytm)}>
                      <ZapIcon /><span>Paytm</span>
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="step2__upi-pay-btn"
                  onClick={handleUpiPay}
                  aria-label={`Open UPI app and pay ₹${order.price.toLocaleString('en-IN')}`}
                >
                  <ZapIcon />
                  <span>Open UPI App &mdash; Pay &#8377;{order.price.toLocaleString('en-IN')}</span>
                </button>
              )}

              <UpiIdRow />
              <AppsHint />

              {/* Collapsible QR for sharing */}
              <div className="step2__qr-collapse-wrap">
                <button
                  type="button"
                  className={`step2__qr-toggle ${qrExpanded ? 'step2__qr-toggle--open' : ''}`}
                  onClick={() => setQrExpanded(v => !v)}
                  aria-expanded={qrExpanded}
                  aria-controls="qr-collapse"
                >
                  <QrCodeIcon />
                  <span>{qrExpanded ? 'Hide QR Code' : 'Show QR Code'} (scan with another phone)</span>
                  <span className="step2__qr-toggle-chevron" aria-hidden="true">
                    <ChevronDown />
                  </span>
                </button>

                <div
                  id="qr-collapse"
                  className={`step2__qr-collapse ${qrExpanded ? 'step2__qr-collapse--open' : ''}`}
                  aria-hidden={!qrExpanded}
                >
                  <div className="step2__qr-collapse-inner">
                    <div className="step2__qr-wrapper" role="img" aria-label={`UPI QR code to pay ₹${order.price.toLocaleString('en-IN')} to ${GETNET.name}`}>
                      <div className="step2__qr-bg">
                        <QRCodeSVG
                          value={upiUri}
                          size={170}
                          bgColor="transparent"
                          fgColor="#F8FAFC"
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      <div className="step2__qr-amount-badge" aria-hidden="true">
                        &#8377;{order.price.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PaidBtn />
            </>
          ) : (
            /* ════════════ DESKTOP LAYOUT ════════════ */
            <>
              <div className="step2__qr-header">
                <PhonePayIcon />
                <span>Scan &amp; Pay</span>
              </div>

              {/* QR Code always visible on desktop */}
              <div className="step2__qr-wrapper" role="img" aria-label={`UPI QR code to pay ₹${order.price.toLocaleString('en-IN')} to ${GETNET.name}`}>
                <div className="step2__qr-bg">
                  <QRCodeSVG
                    value={upiUri}
                    size={190}
                    bgColor="transparent"
                    fgColor="#F8FAFC"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <div className="step2__qr-amount-badge" aria-hidden="true">
                  &#8377;{order.price.toLocaleString('en-IN')}
                </div>
              </div>

              <UpiIdRow />
              <AppsHint />
              <PaidBtn />
            </>
          )}

        </div>
      </div>

      {/* Back */}
      <div className="step2__back-wrap">
        <button type="button" className="step2__back-btn" onClick={onBack} aria-label="Go back to previous step">
          <ChevronLeft />
          Back
        </button>
      </div>
    </section>
  )
}

export default Step2Summary
